import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import usePageMeta from "@/hooks/usePageMeta";
import { useAuth } from "@/contexts/AuthContext";

const AU_STATES = ["NSW", "VIC", "QLD", "WA", "SA", "TAS", "ACT", "NT"];
const SURFACES = ["walls", "ceilings", "doors", "trims", "fences", "garage_doors", "roofing", "decks"];
const PROPERTY_TYPES = ["house", "townhouse", "apartment", "commercial"];

const EMPTY = {
  business_name: "",
  abn: "",
  owner_name: "",
  contact_email: "",
  contact_phone: "",
  website_url: "",
  hq_suburb: "",
  hq_state: "QLD",
  hq_postcode: "",
  service_scope: ["interior", "exterior"],
  service_surfaces: [],
  property_types: [],
  service_postcodes: "",
  licence_reference: "",
  insurance_reference: "",
  account_email: "",
  account_password: "",
  accepted_code_of_conduct: false,
};

export default function PainterApply() {
  usePageMeta({
    title: "Join ColourMatch Pro — Painter application",
    description: "Apply to be listed on Australia's fastest-growing paint visualisation platform. Verified painters appear in homeowner search results and receive pre-qualified quote requests.",
    path: "/painters/apply",
  });

  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [submitted, setSubmitted] = useState(null);

  // Owner accounts bypass the application form entirely — they land straight
  // in the /painter dashboard in owner-preview mode. We wait for the auth
  // probe to resolve (user === null means loading in AuthContext) before
  // deciding, and we return null while an owner is being redirected so the
  // form is never rendered, submitted, or triggers HTML5 validation for
  // owners under any timing.
  const { user } = useAuth();
  const nav = useNavigate();
  useEffect(() => {
    if (user && user.is_owner) {
      nav("/painter", { replace: true });
    }
  }, [user, nav]);

  if (user === null) return null; // auth probe still loading
  if (user && user.is_owner) return null; // owner — redirecting

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggle = (k, item) =>
    setForm((f) => {
      const list = f[k] || [];
      return {
        ...f,
        [k]: list.includes(item) ? list.filter((x) => x !== item) : [...list, item],
      };
    });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const payload = {
        ...form,
        service_postcodes: form.service_postcodes
          .split(/[,\s]+/)
          .map((s) => s.trim())
          .filter(Boolean),
      };
      const r = await api.post("/painters/apply", payload);
      setSubmitted(r.data);
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setBusy(false);
    }
  };

  if (submitted) {
    return (
      <div className="max-w-2xl mx-auto px-5 lg:px-8 py-16 lg:py-24" data-testid="painter-apply-success">
        <div className="overline mb-3">Application received</div>
        <h1 className="font-serif text-4xl sm:text-5xl text-brand-ink tracking-tight mb-4">
          Thanks — we'll review within 3 business days.
        </h1>
        <p className="text-base text-brand-muted leading-relaxed mb-6">
          We've sent a confirmation email. Once a reviewer makes a decision, you'll get
          a notification with next steps. If we need anything else, we'll reach out by
          email.
        </p>
        <p className="text-sm text-brand-muted">
          Reference: <span className="font-mono">{submitted.id}</span>
        </p>
        <Link to="/" className="inline-block mt-8 px-5 py-3 bg-brand-navy text-white text-sm">
          Back to ColourMatch
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-5 lg:px-8 py-12 lg:py-20" data-testid="painter-apply-page">
      <div className="overline mb-3">ColourMatch Pro</div>
      <h1 className="font-serif text-4xl sm:text-5xl text-brand-ink tracking-tight mb-3">
        Apply to join the verified painter directory
      </h1>
      <p className="text-base text-brand-muted leading-relaxed mb-10">
        We're onboarding the first cohort of Brisbane and South-East Queensland painters.
        ColourMatch Pro is <strong>free during launch</strong> — there's no fee while we
        validate demand with our painter partners.
      </p>

      <div className="grid sm:grid-cols-2 gap-6 mb-12" data-testid="apply-explainer">
        <div className="border-l-2 border-brand-teal pl-4">
          <div className="overline mb-2">What we look for</div>
          <p className="text-sm text-brand-ink leading-relaxed">
            Active ABN, current trade licence and public liability insurance, three or
            more photos of recent work, and a willingness to honour the ColourMatch Pro
            Code of Conduct.
          </p>
        </div>
        <div className="border-l-2 border-brand-teal pl-4">
          <div className="overline mb-2">Typical review time</div>
          <p className="text-sm text-brand-ink leading-relaxed">
            Most applications are decided within <strong>3 business days</strong>. We'll
            email you the moment a reviewer makes a decision — or if we need anything
            else to finalise your listing.
          </p>
        </div>
      </div>

      {err && (
        <div className="mb-6 px-4 py-3 border border-red-200 bg-red-50 text-sm text-red-700" data-testid="apply-error">
          {err}
        </div>
      )}

      <form onSubmit={submit} className="space-y-10">
        {/* About */}
        <Section title="About your business">
          <Input id="business_name" label="Business name" value={form.business_name} onChange={(v) => setF("business_name", v)} required />
          <Input id="abn" label="ABN (11 digits)" value={form.abn} onChange={(v) => setF("abn", v)} required placeholder="12 345 678 901" />
          <Input id="owner_name" label="Owner / principal name" value={form.owner_name} onChange={(v) => setF("owner_name", v)} required />
          <div className="grid sm:grid-cols-2 gap-4">
            <Input id="contact_email" label="Business contact email" type="email" value={form.contact_email} onChange={(v) => setF("contact_email", v)} required />
            <Input id="contact_phone" label="Business contact phone" value={form.contact_phone} onChange={(v) => setF("contact_phone", v)} required placeholder="+61 ..." />
          </div>
          <Input id="website_url" label="Website (optional)" value={form.website_url} onChange={(v) => setF("website_url", v)} placeholder="https://" />
        </Section>

        {/* Service */}
        <Section title="Services you offer">
          <ChipGroup id="service_scope" label="Scope" options={["interior", "exterior"]} selected={form.service_scope} onToggle={(v) => toggle("service_scope", v)} required />
          <ChipGroup id="service_surfaces" label="Surfaces" options={SURFACES} selected={form.service_surfaces} onToggle={(v) => toggle("service_surfaces", v)} />
          <ChipGroup id="property_types" label="Property types" options={PROPERTY_TYPES} selected={form.property_types} onToggle={(v) => toggle("property_types", v)} />
        </Section>

        {/* Areas */}
        <Section title="Where you work">
          <div className="grid sm:grid-cols-3 gap-4">
            <Input id="hq_suburb" label="HQ suburb" value={form.hq_suburb} onChange={(v) => setF("hq_suburb", v)} required />
            <div>
              <label htmlFor="hq_state" className="overline mb-2 block">State</label>
              <select id="hq_state" data-testid="field-hq_state" value={form.hq_state} onChange={(e) => setF("hq_state", e.target.value)} className="w-full border border-brand-linen p-3 text-sm">
                {AU_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <Input id="hq_postcode" label="Postcode" value={form.hq_postcode} onChange={(v) => setF("hq_postcode", v)} required />
          </div>
          <Input id="service_postcodes" label="Service postcodes (comma separated)" value={form.service_postcodes} onChange={(v) => setF("service_postcodes", v)} placeholder="4000, 4001, 4101" />
        </Section>

        {/* Credentials */}
        <Section title="Credentials">
          <Input id="licence_reference" label="Trade licence number (state-issued)" value={form.licence_reference} onChange={(v) => setF("licence_reference", v)} />
          <Input id="insurance_reference" label="Public liability insurance reference" value={form.insurance_reference} onChange={(v) => setF("insurance_reference", v)} />
          <p className="text-xs text-brand-muted">
            We'll request a copy of your insurance certificate before approving your listing.
          </p>
        </Section>

        {/* Account */}
        <Section title="Your account">
          <Input id="account_email" label="Login email" type="email" value={form.account_email} onChange={(v) => setF("account_email", v)} required />
          <Input id="account_password" label="Password (at least 8 characters)" type="password" value={form.account_password} onChange={(v) => setF("account_password", v)} required minLength={8} />
          <label className="flex items-start gap-3 mt-2 cursor-pointer">
            <input
              type="checkbox"
              checked={form.accepted_code_of_conduct}
              onChange={(e) => setF("accepted_code_of_conduct", e.target.checked)}
              data-testid="field-accepted_code_of_conduct"
              className="mt-1"
            />
            <span className="text-sm text-brand-ink leading-relaxed">
              I have read and agree to the{" "}
              <Link to="/painters/code-of-conduct" target="_blank" className="text-brand-teal underline">
                ColourMatch Pro Painter Code of Conduct
              </Link>{" "}
              <span className="text-brand-muted">(draft for review)</span>.
            </span>
          </label>
        </Section>

        <button
          type="submit"
          disabled={busy || !form.accepted_code_of_conduct}
          data-testid="submit-application"
          className="w-full sm:w-auto px-8 py-4 bg-brand-navy text-white text-sm disabled:opacity-50"
        >
          {busy ? "Submitting…" : "Submit application"}
        </button>
      </form>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <fieldset className="border-t border-brand-linen pt-8 space-y-4">
      <legend className="overline mb-3">{title}</legend>
      {children}
    </fieldset>
  );
}

function Input({ id, label, value, onChange, type = "text", required, placeholder, minLength }) {
  return (
    <div>
      <label htmlFor={id} className="overline mb-2 block">{label}</label>
      <input
        id={id}
        data-testid={`field-${id}`}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        placeholder={placeholder}
        minLength={minLength}
        className="w-full border border-brand-linen p-3 text-sm"
      />
    </div>
  );
}

function ChipGroup({ id, label, options, selected, onToggle, required }) {
  return (
    <div>
      <label className="overline mb-2 block">
        {label}{required && <span className="text-brand-teal"> *</span>}
      </label>
      <div className="flex flex-wrap gap-2" data-testid={`chips-${id}`}>
        {options.map((opt) => {
          const active = selected.includes(opt);
          return (
            <button
              type="button"
              key={opt}
              data-testid={`chip-${id}-${opt}`}
              onClick={() => onToggle(opt)}
              className={`px-3 py-1.5 text-sm border ${active ? "bg-brand-navy text-white border-brand-teal" : "border-brand-linen text-brand-ink hover:bg-brand-linen/30"}`}
            >
              {opt.replace("_", " ")}
            </button>
          );
        })}
      </div>
    </div>
  );
}
